import { Link } from "react-router-dom";
import { Clock, RotateCcw, AlertCircle, DollarSign, Truck, Phone, HelpCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Seo from "@/components/Seo";

const faqSections = [
  {
    category: "Ordering & Timing",
    icon: <Clock className="text-[#b8966a]" size={20} />,
    questions: [
      {
        q: "How far in advance do I need to place my order?",
        a: "All orders must be placed at least 72 hours in advance of your desired delivery date. This ensures we have time to source the freshest blooms and craft your arrangement with the care and attention it deserves."
      },
      {
        q: "What happens if I need an order in less than 72 hours?",
        a: "We understand that sometimes life calls for last-minute beauty. Orders placed within the 72-hour window will incur a $50 rush fee to accommodate the expedited sourcing and preparation. Rush orders are subject to availability."
      },
      {
        q: "Can I schedule a recurring order?",
        a: "Absolutely! Our Monthly Noir Subscription delivers a curated seasonal arrangement to your door each month. You can also set up custom recurring orders through our Inquiries page."
      }
    ]
  },
  {
    category: "Returns & Issues",
    icon: <RotateCcw className="text-[#b8966a]" size={20} />,
    questions: [
      {
        q: "Do you accept returns?",
        a: "Due to the perishable nature of our products, we do not accept returns. Each arrangement is handcrafted with the freshest blooms available, making returns impractical for quality assurance."
      },
      {
        q: "What if there's an issue with my order?",
        a: "Your satisfaction is important to us. If there is any issue with your arrangement — whether it's damage during delivery, quality concerns, or anything else — please reach out to us as soon as possible and we'll do our best to fix it. Contact us at hello@fleurisnoir.com or call (612) 547-9260."
      },
      {
        q: "What if my delivery arrives damaged?",
        a: "Please contact us within 24 hours of delivery with photos of the damage. We'll work with you to make it right, whether that means a replacement arrangement or another solution."
      }
    ]
  },
  {
    category: "Pricing & Payment",
    icon: <DollarSign className="text-[#b8966a]" size={20} />,
    questions: [
      {
        q: "What are your bouquet prices?",
        a: "Our signature Rosé bouquets range from $85 to $350 depending on quantity:\n\n• 25 Roses — $85\n• 50 Roses — $160\n• 75 Roses — $250\n• 125 Roses — $350\n\nCustom arrangements and event packages are priced based on your specific needs."
      },
      {
        q: "What payment methods do you accept?",
        a: "We accept all major credit and debit cards through our secure Stripe checkout. PayPal support is coming soon."
      },
      {
        q: "Is there a rush fee?",
        a: "Yes. Orders placed less than 72 hours before the requested delivery date will have a $50 rush fee added. We recommend planning ahead to avoid this charge."
      }
    ]
  },
  {
    category: "Delivery",
    icon: <Truck className="text-[#b8966a]" size={20} />,
    questions: [
      {
        q: "Do you deliver?",
        a: "Yes! We offer local delivery within the Minneapolis area. Delivery is available Tuesday through Saturday. Specific delivery windows can be arranged at checkout or through our Inquiries page."
      },
      {
        q: "What areas do you deliver to?",
        a: "We deliver throughout the Minneapolis area and the greater metro. For locations outside our standard delivery zone, please contact us and we'll do our best to accommodate."
      }
    ]
  }
];

export default function FAQ() {
  return (
    <div className="page-transition pt-[72px]" data-testid="faq-page">
      <Seo
        title="Delivery, Ordering & FAQ | Fleuris Noir — Minneapolis Florist"
        description="Answers about Fleuris Noir luxury flower delivery in Minneapolis — ordering lead times, delivery days and areas, care, and how to reach us."
        path="/faq"
        jsonLd={{
          "@context": "https://schema.org", "@type": "FAQPage",
          mainEntity: faqSections.flatMap((s) => s.questions).map(({ q, a }) => ({
            "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a },
          })),
        }}
      />
      {/* Header */}
      <section className="section bg-[#0c0c0c] border-b border-[#b8966a]/10">
        <div className="container-custom text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <HelpCircle size={18} className="text-[#b8966a]" />
            <p className="overline">Questions Answered</p>
          </div>
          <h1 className="font-display text-4xl md:text-5xl text-[#f8f5f0] mb-4">
            Frequently Asked Questions
          </h1>
          <p className="font-body text-[#a19d98] max-w-2xl mx-auto">
            Everything you need to know about ordering from Fleuris Noir. 
            Can't find what you're looking for? Don't hesitate to reach out.
          </p>
        </div>
      </section>

      {/* Important Notices */}
      <section className="py-12 bg-[#161616] border-b border-[#b8966a]/10">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-[#0c0c0c] border border-[#b8966a]/20 p-6 flex gap-4" data-testid="faq-notice-72hr">
              <AlertCircle className="text-[#b8966a] flex-shrink-0 mt-1" size={22} />
              <div>
                <h3 className="font-heading text-lg text-[#f8f5f0] mb-2">72-Hour Advance Notice</h3>
                <p className="font-body text-sm text-[#a19d98]">
                  All orders must be placed at least 72 hours in advance. 
                  Orders within this window will have a <span className="text-[#b8966a] font-medium">$50 rush fee</span> applied.
                </p>
              </div>
            </div>
            <div className="bg-[#0c0c0c] border border-[#b8966a]/20 p-6 flex gap-4" data-testid="faq-notice-returns">
              <RotateCcw className="text-[#b8966a] flex-shrink-0 mt-1" size={22} />
              <div>
                <h3 className="font-heading text-lg text-[#f8f5f0] mb-2">No Returns Policy</h3>
                <p className="font-body text-sm text-[#a19d98]">
                  We do not accept returns. If there's an issue with your order, 
                  please <Link to="/inquiries" className="text-[#b8966a] hover:text-[#cfae82] transition-colors underline">reach out to us</Link> and we'll do our best to fix it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="section bg-[#0c0c0c]">
        <div className="container-custom max-w-4xl">
          {faqSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-12 last:mb-0">
              <div className="flex items-center gap-3 mb-6">
                {section.icon}
                <h2 className="font-heading text-2xl text-[#f8f5f0]">
                  {section.category}
                </h2>
              </div>

              <Accordion type="single" collapsible className="space-y-2">
                {section.questions.map((item, qIndex) => (
                  <AccordionItem 
                    key={qIndex} 
                    value={`${sectionIndex}-${qIndex}`}
                    className="border border-[#b8966a]/10 bg-[#161616] px-6 data-[state=open]:border-[#b8966a]/30 transition-colors"
                    data-testid={`faq-item-${sectionIndex}-${qIndex}`}
                  >
                    <AccordionTrigger className="py-5 font-heading text-lg text-[#f8f5f0] hover:text-[#b8966a] transition-colors hover:no-underline">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="font-body text-[#a19d98] leading-relaxed pb-5 whitespace-pre-line">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="section bg-[#161616] border-t border-[#b8966a]/10">
        <div className="container-custom text-center max-w-2xl">
          <Phone className="text-[#b8966a] mx-auto mb-4" size={28} />
          <h2 className="font-heading text-2xl text-[#f8f5f0] mb-4">
            Still Have Questions?
          </h2>
          <p className="font-body text-[#a19d98] mb-8">
            We're here to help. Reach out and our team will get back to you within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/inquiries" className="btn-primary" data-testid="faq-contact-btn">
              Contact Us
            </Link>
            <a href="tel:+16125479260" className="btn-secondary">
              Call (612) 547-9260
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
